import type { ApplicationFilters } from "./queries/useApplications";

// ---------------------------------------------------------------------------
// Centralised query key factory
// Keeps cache management consistent across all hooks.
// ---------------------------------------------------------------------------

export const queryKeys = {
  // Job Postings
  jobPostings: {
    all: ["jobPostings"] as const,
    lists: () => [...queryKeys.jobPostings.all, "list"] as const,
    list: () => [...queryKeys.jobPostings.lists()] as const,
    details: () => [...queryKeys.jobPostings.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.jobPostings.details(), id] as const,
  },

  // Applications
  applications: {
    all: ["applications"] as const,
    lists: () => [...queryKeys.applications.all, "list"] as const,
    list: (filters?: ApplicationFilters) =>
      [...queryKeys.applications.lists(), filters ?? {}] as const,
    details: () => [...queryKeys.applications.all, "detail"] as const,
    detail: (id: string, persona?: "manager" | "candidate") =>
      [...queryKeys.applications.details(), id, persona ?? "candidate"] as const,
  },

  // Pipeline
  pipeline: {
    all: ["pipeline"] as const,
    lists: () => [...queryKeys.pipeline.all, "list"] as const,
    list: (status?: string) =>
      [...queryKeys.pipeline.lists(), status ?? "all"] as const,
  },

  // Interviews
  interviews: {
    all: ["interviews"] as const,
    lists: () => [...queryKeys.interviews.all, "list"] as const,
    list: () => [...queryKeys.interviews.lists()] as const,
    details: () => [...queryKeys.interviews.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.interviews.details(), id] as const,
  },

  // Campaigns
  campaigns: {
    all: ["campaigns"] as const,
    lists: () => [...queryKeys.campaigns.all, "list"] as const,
    list: () => [...queryKeys.campaigns.lists()] as const,
    details: () => [...queryKeys.campaigns.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.campaigns.details(), id] as const,
  },

  // Dashboard
  dashboard: {
    all: ["dashboard"] as const,
    data: () => [...queryKeys.dashboard.all, "data"] as const,
  },

  // Profile
  profile: {
    all: ["profile"] as const,
    data: () => [...queryKeys.profile.all, "data"] as const,
  },

  // Manager Media
  managerMedia: {
    all: ["managerMedia"] as const,
    lists: () => [...queryKeys.managerMedia.all, "list"] as const,
    list: (applicationId: string) => [...queryKeys.managerMedia.lists(), applicationId] as const,
    details: () => [...queryKeys.managerMedia.all, "detail"] as const,
    detail: (mediaId: string) => [...queryKeys.managerMedia.details(), mediaId] as const,
  },

  // Candidate Media (candidate-facing: GET/DELETE /media/{id})
  candidateMedia: {
    all: ["candidateMedia"] as const,
    details: () => [...queryKeys.candidateMedia.all, "detail"] as const,
    detail: (mediaId: string) => [...queryKeys.candidateMedia.details(), mediaId] as const,
  },

  // Manager Availability
  managerAvailability: {
    all: ["managerAvailability"] as const,
    detail: () => [...queryKeys.managerAvailability.all, "detail"] as const,
  },
} as const;
